import { type ReactNode } from 'react'
import Link from 'next/link'
import { marketplaceLabels, MarketplaceTag, shipmentTypeLabels, type FulfillmentApi } from '@entities/fulfillment'
import { requestFileHref, requestStatusBadges, type FulfillmentRequestApi } from '@entities/fulfillment-request'
import { Badge } from '@ui/base/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@ui/base/card'
import { pluralize } from '@utils/formaters'
import { typeClass } from '@utils/typography'

import { countLabel, dateLabel, fileSizeLabel, priceLabel } from '../lib/format'

const mutedSupporting = `${typeClass.supporting} text-muted-foreground`

/** Пометка состояния заявки. У одобренной её нет: заявка идёт своим ходом, объяснять нечего. */
const statusBadge = (status: FulfillmentRequestApi.Status) => {
  const badge = requestStatusBadges[status]

  return badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : null
}

/** Строки «о заявке»: города забора, округа доставки, объём и срок — то, по чему фулфилмент считает цену. */
const requestFacts = (request: FulfillmentRequestApi.FulfillmentRequest) => {
  const facts: string[] = []
  if (request.cities.length > 0) {
    facts.push(request.cities.map((city) => city.name).join(', '))
  }
  const federalDistricts = request.federal_districts ?? []
  if (federalDistricts.length > 0) {
    // Предлог отличает округа доставки от городов забора выше по строке: без него это одна цепочка мест.
    facts.push(`в ${federalDistricts.map((district) => district.name).join(', ')}`)
  }
  if (request.unit_count !== null) {
    facts.push(`${countLabel(request.unit_count)} ед.`)
  }
  const dueDate = dateLabel(request.due_date)
  if (dueDate) {
    facts.push(`до ${dueDate}`)
  }
  if (request.needs_twice_a_day) {
    facts.push('нужны две отгрузки в день')
  }

  return facts
}

/** Схемы работы короткими подписями: FBO, FBS, DBS. */
const requestShipmentSummary = (request: FulfillmentRequestApi.FulfillmentRequest) =>
  request.shipment_types.map((type) => shipmentTypeLabels[type]).join(', ')

/** Площадку «другая» подписываем тем названием, которое ввёл продавец. */
export const RequestMarketplaces = ({ request }: { request: FulfillmentRequestApi.FulfillmentRequest }) => (
  <div className="flex flex-wrap gap-1.5">
    {request.marketplaces.map((marketplace) => (
      <MarketplaceTag
        key={marketplace}
        marketplace={marketplace as FulfillmentApi.Marketplace}
        label={
          marketplace === 'other' && request.marketplace_other_name
            ? request.marketplace_other_name
            : (marketplaceLabels[marketplace as FulfillmentApi.Marketplace] ?? marketplace)
        }
      />
    ))}
  </div>
)

const isPhoto = (file: FulfillmentRequestApi.File) => file.mime_type.startsWith('image/')

/**
 * Файлы отдаёт бэкенд, поэтому ссылка ведёт через прокси фронтенда — он передаёт куки.
 * Фотографии товара показываем картинками: по ним фулфилмент понимает заявку быстрее, чем по имени файла.
 */
export const RequestFiles = ({ requestId, files }: { requestId: string; files: FulfillmentRequestApi.File[] }) => {
  const photos = files.filter(isPhoto)
  const documents = files.filter((file) => !isPhoto(file))

  return (
    <div className="grid gap-2">
      <span className={typeClass.label}>Файлы</span>
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((photo) => {
            const href = requestFileHref(requestId, photo.id)

            return (
              <a key={photo.id} href={href} target="_blank" rel="noopener noreferrer" title={photo.name}>
                {/* Размеры снимка заранее неизвестны, поэтому обычный img вместо next/image. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={href} alt={photo.name} loading="lazy" className="size-20 rounded-md border object-cover" />
              </a>
            )
          })}
        </div>
      )}
      {documents.map((file) => (
        <a
          key={file.id}
          href={requestFileHref(requestId, file.id)}
          className={`${typeClass.body} underline underline-offset-2`}
        >
          {file.name} <span className={mutedSupporting}>{fileSizeLabel(file.size)}</span>
        </a>
      ))}
    </div>
  )
}

export const RequestOffers = ({ offers }: { offers: FulfillmentRequestApi.Offer[] }) => {
  const newCount = offers.filter((offer) => offer.is_new).length

  return (
    <div className="grid gap-2">
      <span className={`${typeClass.label} flex flex-wrap items-center gap-2`}>
        Ответы фулфилментов ({offers.length})
        {newCount > 0 && (
          <Badge variant="destructive">
            {newCount} {pluralize(newCount, ['новый', 'новых', 'новых'])}
          </Badge>
        )}
      </span>
      {offers.map((offer) => (
        // Новый ответ обведён рамкой цвета акцента: его видно, не читая весь список.
        <div key={offer.id} className={`grid gap-1 rounded-lg border p-3 ${offer.is_new ? 'border-primary' : ''}`}>
          <span className={`${typeClass.label} flex flex-wrap items-center gap-2`}>
            {offer.fulfillment_slug ? (
              <Link href={`/fulfillment/${offer.fulfillment_slug}`}>{offer.fulfillment_name}</Link>
            ) : (
              offer.fulfillment_name
            )}
            {offer.is_new && <Badge variant="destructive">Новый</Badge>}
          </span>
          <span className={typeClass.body}>{priceLabel(offer.price) ?? 'Цена не указана'}</span>
          {offer.comment && <span className={mutedSupporting}>{offer.comment}</span>}
          {offer.is_outdated && <Badge variant="warning">Цена рассчитана по прежней версии заявки</Badge>}
        </div>
      ))}
    </div>
  )
}

export const RequestCard = ({
  request,
  children,
}: {
  request: FulfillmentRequestApi.FulfillmentRequest
  children?: ReactNode
}) => (
  <Card role="article" aria-label={request.title}>
    <CardHeader className="grid gap-1 border-b">
      <CardTitle>{request.title}</CardTitle>
      <span className={mutedSupporting}>
        {[requestShipmentSummary(request), ...requestFacts(request)].filter(Boolean).join(' · ')}
      </span>
    </CardHeader>
    <CardContent className="grid gap-4">
      {request.marketplaces.length > 0 && <RequestMarketplaces request={request} />}
      {request.description && <p className={typeClass.body}>{request.description}</p>}
      {request.files.length > 0 && <RequestFiles requestId={request.id} files={request.files} />}
      {statusBadge(request.status)}
      {children}
    </CardContent>
  </Card>
)
